'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Assignment,
  AssignmentInd,
  TrackChanges,
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
  Save,
  Category,
  LibraryBooks,
  AccountTree,
  FactCheck,
  FindInPage,
  FileDownload,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = ['Build the Library', 'Map to Survey Risk', 'Assign', 'Complete', 'Act on Results'];

const painPoints = [
  'CEP PDFs printed and filed, but rarely completed as real reviews',
  'No assignment or due dates—pathway reviews happen only when someone has time',
  'No record of which pathways were completed, by whom, or what was found',
  'No connection between pathways and the F-tags and focus areas surveys actually cite',
  'Weak pathway results that never trigger corrective action or improvement work',
  'Survey prep that starts from zero because there\'s no pathway review history',
];

const capabilities = [
  {
    icon: LibraryBooks,
    title: 'Configurable Pathway Library',
    description: 'Maintain your Critical Element Pathway forms in the Form Wizard—sections, questions, and attachments configured to match the pathways your surveys use—with state scoping and go-live and retirement dates for version control.',
  },
  {
    icon: AccountTree,
    title: 'F-Tag & Focus Area Mapping',
    description: 'Link each pathway to survey focus areas, the F-tags it protects, and the CMS requirements of participation it addresses—so pathway work maps directly to survey risk, not generic checklists.',
  },
  {
    icon: AssignmentInd,
    title: 'Assignment to User, Role, or Committee',
    description: 'Assign each pathway review to a specific user, an entire role, or a committee—with a category, type, priority, assigned date, and due date. Pathway reviews become owned work, not optional reading.',
  },
  {
    icon: Assignment,
    title: 'My CEP Workqueue',
    description: 'Assignees see their pathways in dedicated New, In Progress, and Completed queues—filterable by priority, category, type, and date ranges—and complete them in the same form experience used across the platform.',
  },
  {
    icon: Save,
    title: 'Draft & Complete Lifecycle',
    description: 'Staff save pathway work in progress and return to it, with completion status and completion date recorded when the review is done—so long pathways don\'t have to be finished in one sitting.',
  },
  {
    icon: FactCheck,
    title: 'Completion Tracking',
    description: 'Management sees every assigned pathway\'s status—new, in progress, or completed—by facility, with assignment details and completion dates, so pathway coverage is provable rather than assumed.',
  },
  {
    icon: TrackChanges,
    title: 'KPI Targets & Automated Triggers',
    description: 'Define KPI metric targets on pathway results and configure triggers that generate an action plan or a performance improvement plan—with a name, due window, and assignee—when results miss target. Weak findings become work, automatically.',
  },
  {
    icon: Category,
    title: 'Pathway Categories & Types',
    description: 'Organize pathways with configurable categories and types that match your survey readiness program—clinical, environmental, resident rights, and whatever structure fits your organization.',
  },
  {
    icon: FindInPage,
    title: 'Mock Survey Alignment',
    description: 'Pathway and focus-area mappings feed mock survey planning, so internal survey exercises target the same pathways a real survey team would pull.',
  },
];

const roles = [
  {
    title: 'Administrators & DONs',
    benefit: 'Provable pathway coverage across the facility, with completion visibility',
  },
  {
    title: 'Survey readiness leaders',
    benefit: 'Pathways mapped to F-tags and focus areas, aligned with mock survey planning',
  },
  {
    title: 'QAPI & quality leaders',
    benefit: 'KPI triggers that turn weak pathway results into action plans and PIPs',
  },
  {
    title: 'Department heads & assignees',
    benefit: 'A clear queue of assigned pathways with priorities, due dates, and draft saving',
  },
  {
    title: 'Corporate / multi-site operators',
    benefit: 'A consistent pathway library and review cadence across every facility',
  },
];

const outcomes = [
  { value: 'Tracked', label: 'Pathway coverage', detail: 'Which pathways are assigned, in progress, and completed—by facility' },
  { value: 'Assigned', label: 'Clear ownership', detail: 'Every pathway review owned by a user, role, or committee with a due date' },
  { value: 'Aligned', label: 'Survey-risk alignment', detail: 'Pathways mapped to the F-tags and focus areas surveys cite' },
  { value: 'Resolved', label: 'Closed-loop follow-up', detail: 'KPI triggers converting missed targets into action plans and PIPs' },
  { value: 'Logged', label: 'Review history', detail: 'Completed pathway records with dates and responses, ready for survey prep' },
  { value: 'Standard', label: 'Program consistency', detail: 'One pathway library and process across all facilities' },
];

const integrations = [
  'Form Wizard',
  'Survey Management',
  'Mock Surveys',
  'Action Plans',
  'Performance Improvement Plans',
  'Committees',
];

const faqs = [
  {
    q: 'What are Critical Element Pathways?',
    a: 'Critical Element Pathways are the structured investigative protocols surveyors use to evaluate specific care areas—dining, infection control, pressure ulcers, and many more. CareAscendium lets your team run those same pathways internally as assigned, tracked reviews.',
  },
  {
    q: 'Can we customize the pathway forms?',
    a: 'Yes. Pathways are built in the Form Wizard, so you control the sections, questions, and attachments—and can scope forms by state and manage versions with go-live and retirement dates.',
  },
  {
    q: 'Who completes a pathway review?',
    a: 'Whoever you assign—a specific user, an entire role, or a committee. Assignees work pathways from their My CEP queue with priorities and due dates, saving drafts along the way.',
  },
  {
    q: 'How do pathways connect to survey risk?',
    a: 'Each pathway links to survey focus areas, F-tags, and CMS requirements of participation—and those mappings also feed mock survey planning.',
  },
  {
    q: 'What happens when a pathway finds a problem?',
    a: 'KPI metric targets can be configured with triggers that generate an action plan or a performance improvement plan—with an assignee and due window—when results miss target.',
  },
  {
    q: 'Can management see pathway coverage?',
    a: 'Yes. Every assigned pathway\'s status—new, in progress, completed—is visible by facility with assignment details and completion dates.',
  },
];

export default function CriticalElementPathwaysClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Critical Element Pathways (CEP) Software for Skilled Nursing Facilities | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Critical Element Pathways"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              mx: 'auto',
            }}
          >
            Run the Same Pathways Surveyors Use—Before Surveyors Use Them
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            CareAscendium turns CMS Critical Element Pathways into assigned, due-dated reviews your team completes on configurable forms—mapped to F-tags and survey focus areas, tracked to completion, and wired to KPI triggers that turn weak results into action plans and improvement projects.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>

          </Box>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid item xs={12} md={10} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge with Pathways That Sit in a Binder
            </Typography>
            <Typography sx={{ color: '#666', mb: 4, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
              Surveyors walk in with Critical Element Pathways—structured investigative protocols for dining, infection control, pressure ulcers, and dozens of other focus areas. Most facilities have the same PDFs printed in a binder, but no process for actually working them: nobody is assigned, nothing is due, results aren't tracked, and weak answers never become corrective action.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={10}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;Surveyors will work the pathway on your facility either way. The only question is whether your team worked it first.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              One Workflow. From Assignment to Completion to Action.
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              CareAscendium manages Critical Element Pathways like the operational work they should be. Build your pathway library as configurable forms, assign each pathway to a user, role, or committee with a due date and priority, track completion through dedicated queues, and configure KPI triggers so results that miss target automatically generate action plans or performance improvement projects.
            </Typography>
          </Box>
          <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: 'white' }}>
            <Stepper
              alternativeLabel
              activeStep={-1}
              sx={{
                '& .MuiStepLabel-label': { fontWeight: 600, color: BRAND_BLUE, mt: 1 },
                '& .MuiStepIcon-root': { color: BRAND_BLUE, '&.Mui-completed': { color: BRAND_GREEN } },
              }}
            >
              {workflowSteps.map((label) => (
                <Step key={label} completed>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 4, flexWrap: 'wrap', textAlign: 'center' }}>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', maxWidth: 200 }}>
                 <b>Build the Library:</b> Maintain pathway forms in the Form Wizard, with state scoping and go-live dates
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', maxWidth: 200 }}>
                 <b>Map to Survey Risk:</b> Link pathways to survey focus areas and the F-tags they protect
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', maxWidth: 200 }}>
                 <b>Assign:</b> Route pathways to a user, role, or committee with category, type, priority, and due date
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', maxWidth: 200 }}>
                 <b>Complete:</b> Assignees work pathways from their My CEP queue, saving drafts as they go
               </Typography>
               <Typography sx={{ color: '#666', fontSize: '0.9rem', maxWidth: 200 }}>
                 <b>Act on Results:</b> KPI triggers turn missed targets into action plans and PIPs
               </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {capabilities.map((cap) => (
            <Box key={cap.title}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: `${BRAND_BLUE}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {cap.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Built for Every Role
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  height: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the CareAscendium Platform
                </Typography>
                <Typography sx={{ opacity: 0.9, mb: 3, lineHeight: 1.6 }}>
                  Critical Element Pathways connect survey readiness to the rest of your quality system:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Survey readiness you can schedule.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes & Compliance */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes You Can Measure
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto'
          }}
        >
          {outcomes.map((item) => (
            <Paper
              key={item.label}
              elevation={2}
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND_GREEN, mb: 0.5 }}>
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 0.5, fontSize: '0.95rem' }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>{item.detail}</Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Documentation You Can Stand Behind
          </Typography>
          <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem', mb: 4 }}>
            CareAscendium supports a disciplined, survey-aligned pathway program:
          </Typography>
          <Box sx={{ textAlign: 'left', width: 'fit-content', mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>A maintained pathway library with state scoping and version dates</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Pathway-to-F-tag and focus-area mappings tied to CMS requirements of participation</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Assigned, due-dated reviews with named accountability</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>Completion records with status and dates for every assigned pathway</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>KPI targets with automated triggers into corrective action and improvement work</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>A pathway review history that makes survey preparation cumulative, not chaotic</Typography>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion 
                key={i}
                expanded={expanded === i} 
                onChange={handleChange(i)}
                sx={{ 
                  mb: 2.5, 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 3, pt: 0 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, fontSize: '0.95rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Work the Pathways Before the Survey Does?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              See how CareAscendium turns Critical Element Pathways from binder PDFs into assigned, completed, acted-on reviews that build real survey readiness.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
